import styles from "./agenda-open.module.scss";
import { LegendData, EventLegendData } from "helpers/models";
import { AppContext } from "helpers/context";
import { useState, useContext, useEffect, useRef } from "react";
import { MapContext } from "helpers/map";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from '@mui/material';
import ResizeObserver from 'resize-observer-polyfill';
import { ReactComponent as MyLocationSvg } from "assets/my_location.svg";
import { ReactComponent as MapLegendPinSvg } from "assets/icon_map_legend_pin.svg";
import * as turf from '@turf/turf';

interface DrawShapeLegend {
	shapeId: string;
	lineIndex: number;
	lineColor: string;
	lineName: string;
	description?: string;
}

interface Props {
	onClose?: (item: LegendData) => void;
	onEventsClose?: (item: EventLegendData) => void;
	drawShapeLegends?: DrawShapeLegend[];
	onDrawShapeClose?: (shapeId: string) => void;
}

interface LegendColor {
	raster: {
		color?: string;
		opacity?: number;
	};
	polygon: {
		fill?: string;
		fillOpacity?: number;
		stroke?: string;
		strokeDashArray?: any;
		strokeOpacity?: number;
	};
}

interface LegendItem {
	title: string;
	symbolizers: LegendColor[];
}

function Agenda(props: Readonly<Props>) {
	const [context] = useContext(AppContext);
	const { AMap, map } = useContext(MapContext);
	const agendaEl = useRef<HTMLDivElement>(null);
	const [isAtStart, setIsAtStart] = useState(true); // 是否在起始位置
	const [isAtEnd, setIsAtEnd] = useState(false); // 是否在末尾位置
	const [legendArr, setLegendArr] = useState<any[]>([]);
	const [t] = useTranslation();

	const handleLeftClick = () => {
		if (agendaEl.current) {
			const { scrollLeft } = agendaEl.current;
			// 判断是否在起始位置
			setIsAtStart((scrollLeft - 100) <= 0);
			// 判断是否在末尾位置
			setIsAtEnd(false);
			// 横向滚动 100px，并平滑滚动
			agendaEl.current.scrollBy({ left: -100, behavior: 'smooth' });
		}
	}

	const handleRightClick = () => {
		if (agendaEl.current) {
			const { scrollLeft, scrollWidth, clientWidth } = agendaEl.current;
			// 判断是否在起始位置
			setIsAtStart(false);
			// 判断是否在末尾位置
			setIsAtEnd((scrollLeft + clientWidth + 100) >= scrollWidth);
			// 横向滚动 100px，并平滑滚动
			agendaEl.current.scrollBy({ left: 100, behavior: 'smooth' });
		}
	}

	const handleBtnType = () => {
		if (agendaEl.current) {
			const { scrollLeft, scrollWidth, clientWidth } = agendaEl.current;
			// 判断是否在起始位置
			setIsAtStart(scrollLeft === 0);
			// 判断是否在末尾位置
			setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
		}
	}

	const handleClickMyLocation = () => {
		if (context.currentMultipleLocations && context.currentMultipleLocations.items) {
			let bounds: any = [];
			context.currentMultipleLocations.items.forEach((item: any) => {
				if (item.checked) {
					bounds.push([item.longitude, item.latitude]);
				}
			});
			if (bounds.length === 0) return
			let bbox: any = turf.bboxPolygon(turf.bbox(turf.multiPoint(bounds)));

			let myBounds = new AMap.Bounds([bbox.bbox[2], bbox.bbox[3]], [bbox.bbox[0], bbox.bbox[1]]);
			map?.setBounds(myBounds, true, [20, 20, 20, 20]);
		}
	}

	const handleClickEventLocation = (item: EventLegendData) => {
		const isCross180 = item.layerInfo.extent.minimumLongitude > item.layerInfo.extent.maximumLongitude;
		const southWest = new AMap.LngLat(item.layerInfo.extent.minimumLongitude, item.layerInfo.extent.minimumLatitude, isCross180);
		const northEast = new AMap.LngLat(isCross180 ? item.layerInfo.extent.maximumLongitude + 360 : item.layerInfo.extent.maximumLongitude, item.layerInfo.extent.maximumLatitude, isCross180);
		let myBounds = new AMap.Bounds(southWest, northEast);
		map?.setBounds(myBounds, true, [20, 20, 20, 20]);
	}

	const getLegendArrData = (total: number) => {
		const base = Math.pow(total, 1 / 8);
		let current = 1;
		const thresholds: any = [{
			color: "#fff8a1",
			value: 1
		}];

		let color = ["#fff8a1", "#ffcb8a", "#ff9c86", "#ee6c91", "#cb419c", "#9c2499", "#61168e", "#001789"];
		for (let i = 1; i < color.length; i++) {
			current = current * base;
			thresholds.push({
				color: color[i],
				value: current
			});
			thresholds[i - 1].endValue = current;
		}

		thresholds[7].endValue = total;
		return thresholds;
	}

	const handleStyleDisplay = (item: LegendItem) => {
		if (item.symbolizers[0].raster) {
			return item.symbolizers[0].raster.opacity === 0 ? "none" : "flex"
		} else {
			return item.symbolizers[0].polygon.fillOpacity === 0 ? "none" : "flex"
		}
	}

	const handleLegentTooltip = (item: any, index: number) => {
		let str = `${item.value.toFixed()} - ${item.endValue.toFixed()}(`
		if (index === (legendArr.length - 1)) {
			str += `>${Math.floor(item.value / item.endValue * 100)}%)`
		} else {
			str += `${(item.value.toFixed() == 1 ? 0 : Math.floor((item.value / (context.currentMultipleLocations?.itemCount || 0)) * 100))}-${(item.endValue.toFixed() == 1 ? 0 : Math.floor((item.endValue / (context.currentMultipleLocations?.itemCount || 0)) * 100))}%)`;
		}
		return str
	}

	useEffect(() => {
		let arr = getLegendArrData(context.currentMultipleLocations?.itemCount || 0);
		setLegendArr(arr)
	}, [context.currentMultipleLocations?.itemCount]);

	useEffect(() => {
		const observer = new ResizeObserver((entries) => {
			requestAnimationFrame(() => {
				entries.forEach((entry) => {
					// 根据当前元素宽度变化控制按钮状态
					if (entry.target === agendaEl.current) {
						handleBtnType();
					}
				});
			})
		});

		// 开始监听该元素
		if (agendaEl.current) observer.observe(agendaEl.current);

		// 组件卸载时停止监听
		return () => observer.disconnect();
	}, []);

	return (<>
		<IconButton
			style={{
				flexShrink: 0,
			}}
			sx={{
				width: "36px",
				"&.Mui-disabled": {
					cursor: "no-drop",
					pointerEvents: "auto"
				}
			}}
			disabled={isAtStart}
			onClick={handleLeftClick}
		>
			<div className="material-icons">chevron_left</div>
		</IconButton>
		<div className={styles.agendaContent} ref={agendaEl}>
			<div style={{ display: "flex" }}>
				{context.currentMultipleLocations &&
					(<div className={styles.agendaItem}>
						<div className={styles.header}>
							<div className={styles.text}>{context.currentMultipleLocations.name}</div>
							<MyLocationSvg
								className="w-[16px] h-[16px] fill-[#0009] cursor-pointer"
								onClick={handleClickMyLocation}
							/>
						</div>
						{context.currentMultipleLocations?.itemCount && (context.currentMultipleLocations?.itemCount > 9) ?
							<div className="flex">{
								legendArr.map((item, index) =>
								(<Tooltip
									title={handleLegentTooltip(item, index)}
									placement="top"
									key={item.id}
								>
									<div
										className={styles.legendMutipleItem}
									>
										<MapLegendPinSvg
											className="w-[13px] h-[13px] mr-[3px]"
											style={{ fill: item.color }}
										/>
										<span
											style={{
												backgroundColor: item.color,
												opacity: '1',
												flex: 1,
												height: "6px",
											}}
										></span>
									</div>
								</Tooltip>))
							}</div>
							:
							(<Tooltip title={`100%(${context.currentMultipleLocations?.itemCount})`} placement="top">
								<div
									className={styles.legendMutipleItem}
								>
									<MapLegendPinSvg className="w-[13px] h-[13px] mr-[3px] fill-[#001789]" />
									<span
										style={{
											backgroundColor: "#001789",
											opacity: '1',
											flex: 1,
											height: "6px",
										}}
									></span>
								</div>
							</Tooltip>)}
					</div>)
				}
				{context.eventsLegend &&
					(context.eventsLegend.map((itemLayer, indexLayer) => (
						itemLayer.layerInfo.checked && (
							<div
								className={styles.agendaItem}
								key={itemLayer.title + indexLayer}
							>
								<div className={styles.header}>
									<Tooltip title={itemLayer.layerInfo.name}>
										<div className={styles.text}>{itemLayer.layerInfo.name}</div>
									</Tooltip>
									<div className="flex">
										<MyLocationSvg
											id="loc"
											className="w-[16px] h-[16px] fill-[#0009] cursor-pointer mr-[5px]"
											onClick={() => handleClickEventLocation(itemLayer)}
										/>
										<div
											role="button"
											tabIndex={0}
											className={`material-icons ${styles.icon}`}
											onClick={() => props.onEventsClose!(itemLayer)}
										>
											cancel
										</div>
									</div>
								</div>
								<div className={styles.legend}>
									{itemLayer.rules &&
										itemLayer.rules.map((item: LegendItem, index: number) => (
											<Tooltip
												title={t("legend." + item.title)}
												placement="top"
												key={item.title}
											>
												<div
													className={styles.legendItem}
													style={{
														display: handleStyleDisplay(item),
														alignItems: "center",
													}}
												>
													<span
														style={{
															flex: 1,
															height: "100%",
															backgroundColor: item.symbolizers[0].raster ? item.symbolizers[0].raster.color : item.symbolizers[0].polygon.fill,
															opacity: item.symbolizers[0].raster ? item.symbolizers[0].raster.opacity : item.symbolizers[0].polygon.fillOpacity,
														}}
													></span>
												</div>
											</Tooltip>
										))}
								</div>
							</div>
						)
					)))
				}
				{context.layerLegend &&
					(context.layerLegend.map((itemLayer, indexLayer) => (
						itemLayer.layerInfo.checked && (
							<div
								className={styles.agendaItem}
								key={itemLayer.title + indexLayer}
							>
								<div className={styles.header}>
									<Tooltip title={t("legend." + itemLayer.title)}>
										<div className={styles.text}>{t("legend." + itemLayer.title)}</div>
									</Tooltip>
									<div
										role="button"
										tabIndex={0}
										className={`material-icons ${styles.icon}`}
										onClick={() => props.onClose!(itemLayer)}
									>
										cancel
									</div>
								</div>
								<div className={styles.legend}>
									{itemLayer.rules &&
										itemLayer.rules.map((item: LegendItem, index: number) => (
											<Tooltip
												title={t("legend." + item.title)}
												placement="top"
												key={item.title}
											>
												<div
													className={styles.legendItem}
													style={{
														display: handleStyleDisplay(item),
														alignItems: "center",
													}}
												>
													<span
														style={{
															flex: 1,
															height: "100%",
															backgroundColor: item.symbolizers[0].raster ? item.symbolizers[0].raster.color : item.symbolizers[0].polygon.fill,
															opacity: item.symbolizers[0].raster ? item.symbolizers[0].raster.opacity : item.symbolizers[0].polygon.fillOpacity,
														}}
													></span>
												</div>
											</Tooltip>
										))}
								</div>
							</div>
						)
					)))
				}
				{props.drawShapeLegends?.map((legend) => (
					<div className={styles.agendaItem} key={`draw-shape-${legend.shapeId}`}>
						<div className={styles.header}>
							<Tooltip title={legend.lineName}>
								<div className={styles.text}>{legend.lineName}</div>
							</Tooltip>
							<div
								role="button"
								tabIndex={0}
								className={`material-icons ${styles.icon}`}
								onClick={() => props.onDrawShapeClose?.(legend.shapeId)}
							>
								cancel
							</div>
						</div>
						<div className={styles.legend}>
							<div className={styles.legendItem} style={{ display: "flex", alignItems: "center" }}>
								<span
									style={{
										flex: 1,
										height: "6px",
										backgroundColor: legend.lineColor,
										opacity: 1
									}}
								></span>
							</div>
						</div>
					</div>
				))}
			</div>
		</div >
		<IconButton
			style={{
				flexShrink: 0,
			}}
			sx={{
				width: "36px",
				"&.Mui-disabled": {
					cursor: "no-drop",
					pointerEvents: "auto"
				}
			}}
			disabled={isAtEnd}
			onClick={handleRightClick}
		>
			<div className="material-icons">chevron_right</div>
		</IconButton>
	</>);
}

export default Agenda;

